<template>
  <div class="entity-gallery" :style="{ height: height + 'px' }">
    <el-carousel
      v-if="slides.length > 1"
      class="entity-gallery__swiper"
      :height="height + 'px'"
      :interval="5000"
      arrow="hover"
      indicator-position="inside"
    >
      <el-carousel-item v-for="(img, index) in slides" :key="`${img}-${index}`">
        <img
          :src="img"
          :alt="alt"
          class="entity-gallery__photo"
          @click="openPreview(index)"
        >
      </el-carousel-item>
    </el-carousel>

    <img
      v-else-if="slides.length === 1"
      :src="slides[0]"
      :alt="alt"
      class="entity-gallery__photo"
      @click="openPreview(0)"
    >

    <img
      v-else
      :src="defaultCover"
      :alt="alt"
      class="entity-gallery__default"
    >

    <div
      v-show="previewVisible"
      ref="preview"
      class="entity-gallery-preview"
      @click.self="closePreview"
    >
      <button type="button" class="entity-gallery-preview__close" aria-label="关闭" @click="closePreview">×</button>
      <button
        v-if="slides.length > 1"
        type="button"
        class="entity-gallery-preview__nav entity-gallery-preview__nav--prev"
        aria-label="上一张"
        @click="showPrev"
      >‹</button>
      <img :src="slides[previewIndex]" :alt="alt" class="entity-gallery-preview__image">
      <button
        v-if="slides.length > 1"
        type="button"
        class="entity-gallery-preview__nav entity-gallery-preview__nav--next"
        aria-label="下一张"
        @click="showNext"
      >›</button>
    </div>
  </div>
</template>

<script>
import defaultCover from '@/assets/default-cover-square.png'

export default {
  name: 'EntityGallery',
  props: {
    images: {
      type: Array,
      default: () => []
    },
    alt: {
      type: String,
      default: ''
    },
    height: {
      type: Number,
      default: 320
    }
  },
  data() {
    return {
      defaultCover,
      previewVisible: false,
      previewIndex: 0
    }
  },
  computed: {
    slides() {
      return (this.images || []).filter((url, index, list) => (
        url && list.indexOf(url) === index
      ))
    }
  },
  watch: {
    previewVisible(value) {
      document.body.style.overflow = value ? 'hidden' : ''
    }
  },
  mounted() {
    document.addEventListener('keydown', this.onKeydown)
    if (this.$refs.preview) {
      document.body.appendChild(this.$refs.preview)
    }
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onKeydown)
    document.body.style.overflow = ''
    const preview = this.$refs.preview
    if (preview && preview.parentNode) {
      preview.parentNode.removeChild(preview)
    }
  },
  methods: {
    openPreview(index) {
      this.previewIndex = index
      this.previewVisible = true
    },
    closePreview() {
      this.previewVisible = false
    },
    showPrev() {
      this.previewIndex = (this.previewIndex - 1 + this.slides.length) % this.slides.length
    },
    showNext() {
      this.previewIndex = (this.previewIndex + 1) % this.slides.length
    },
    onKeydown(event) {
      if (!this.previewVisible) {
        return
      }
      if (event.key === 'Escape') {
        this.closePreview()
      } else if (event.key === 'ArrowLeft') {
        this.showPrev()
      } else if (event.key === 'ArrowRight') {
        this.showNext()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.entity-gallery {
  position: relative;
  width: 100%;
  border-radius: 4px;
  background: $color-paper-light;

  &__swiper {
    width: 100%;
    height: 100%;
    overflow: hidden;
    border-radius: 4px;
  }

  &__photo {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
    cursor: zoom-in;
  }

  &__default {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: contain;
    background: $color-brown-dark;
    pointer-events: none;
  }
}

::v-deep .el-carousel__container {
  height: 100% !important;
}

::v-deep .el-carousel__item {
  height: 100%;
}

::v-deep .el-carousel__arrow {
  background: rgba($color-brown-dark, 0.55);
  border: none;

  &:hover {
    background: rgba($color-brown-dark, 0.75);
  }
}

::v-deep .el-carousel__button {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgba($color-white, 0.85);
}

.entity-gallery-preview {
  position: fixed;
  inset: 0;
  z-index: 5000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.86);
  cursor: zoom-out;

  &__image {
    max-width: 88vw;
    max-height: 88vh;
    object-fit: contain;
    box-shadow: 0 12px 48px rgba(0, 0, 0, 0.4);
    cursor: default;
  }

  &__close,
  &__nav {
    position: absolute;
    border: none;
    border-radius: 50%;
    background: rgba($color-white, 0.16);
    color: $color-white;
    cursor: pointer;
    line-height: 1;

    &:hover {
      background: rgba($color-white, 0.28);
    }
  }

  &__close {
    top: 20px;
    right: 28px;
    width: 40px;
    height: 40px;
    font-size: 28px;
  }

  &__nav {
    top: 50%;
    transform: translateY(-50%);
    width: 44px;
    height: 44px;
    font-size: 32px;

    &--prev { left: 24px; }
    &--next { right: 24px; }
  }
}
</style>
